import { Client, type IMessage, type StompSubscription } from '@stomp/stompjs';
// @ts-expect-error - the dist bundle ships no types of its own.
import SockJS from 'sockjs-client/dist/sockjs';
import { API_BASE_URL } from '../config/env';
import {
  normalizePublicDeclaredLoadTracking,
} from './api/publicTracking';
import type { PublicDeclaredLoadTracking } from '../types/publicTracking';

interface PublicTrackingSocketHandlers {
  onSnapshot: (snapshot: PublicDeclaredLoadTracking) => void;
  onConnectionChange?: (connected: boolean) => void;
}

function messagingBaseUrl(): string {
  const base = String(API_BASE_URL ?? '').trim();
  if (!base) throw new Error('Missing API base URL for WebSocket construction');

  const parsed = new URL(base.replace(/^ws(s)?:/i, (match) => (/^wss/i.test(match) ? 'https:' : 'http:')));
  const path = parsed.pathname.replace(/\/+$/, '');
  const messagingPath = path.endsWith('/messaging-service') ? path : `${path}/messaging-service`;

  return `${parsed.origin}${messagingPath}`;
}

function parseBody(message: IMessage): PublicDeclaredLoadTracking | null {
  try {
    return normalizePublicDeclaredLoadTracking(JSON.parse(message.body));
  } catch {
    return null;
  }
}

export function subscribeToPublicDeclaredLoadTracking(
  publicChannelId: string,
  handlers: PublicTrackingSocketHandlers,
): () => void {
  let subscription: StompSubscription | null = null;
  let disposed = false;

  const client = new Client({
    webSocketFactory: () => new SockJS(`${messagingBaseUrl()}/public-ws`) as unknown as WebSocket,
    heartbeatIncoming: 20000,
    heartbeatOutgoing: 20000,
    reconnectDelay: 5000,
    connectionTimeout: 15000,
    onConnect: () => {
      if (disposed) return;
      subscription = client.subscribe(`/topic/declared-load-tracking.${publicChannelId}`, (message) => {
        const snapshot = parseBody(message);
        if (snapshot) handlers.onSnapshot(snapshot);
      });
      handlers.onConnectionChange?.(true);
    },
    onWebSocketClose: () => handlers.onConnectionChange?.(false),
    onStompError: () => handlers.onConnectionChange?.(false),
  });

  client.activate();

  return () => {
    disposed = true;
    try {
      subscription?.unsubscribe();
    } catch {
      /* Already gone with the socket. */
    }
    void client.deactivate();
    handlers.onConnectionChange?.(false);
  };
}
